import { OrderedMap } from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import { LiveStreamEntries, LiveStreamState, type LiveStreamTypesKeys } from '../types';
import type { ReduxState } from 'app/redux/types';

import { createStringFieldSequencer } from 'app/lib/sequencers';

import { sports } from '../../Live/selectors/entities';
import { matchesWithStreamsSelector } from '../../Live/selectors/gridPresentation';
import { LIVE_STREAM_TYPES } from '../constants';
import { liveStreamEntry, type RecordOfLiveStreamEntry } from '../records';

// ---

export const selectLiveStreamState = (state: ReduxState): LiveStreamState => {
  return state.liveStream;
};

const orderByMatchName = createStringFieldSequencer(
  (match: RecordOfLiveStreamEntry) => match.matchName,
);

export const streamsByLiveMatchesSelector = createSelector(
  sports,
  matchesWithStreamsSelector,
  (sports, matchesWithStreams) => {
    return matchesWithStreams.reduce((streamEntries, matchEntity) => {
      const matchId = matchEntity.id;
      const streamType  = matchEntity.attributes.get('streaming')?.type as LiveStreamTypesKeys;
      const streamPayload = matchEntity.attributes.get('streaming')?.payload;

      const streamEntry = liveStreamEntry({
        sportId: matchEntity.sportId,
        matchId,
        isLeading: false,
        type: LIVE_STREAM_TYPES[streamType],
        matchName: matchEntity.name,
        sportName: sports.get(matchEntity.sportId)?.name || '',
        payload: streamPayload,
      });

      return streamEntries.set(matchId, streamEntry);
    }, OrderedMap() as LiveStreamEntries).sort(orderByMatchName);
  },
);

export const selectLiveStreamEntriesFirst = createSelector(
  streamsByLiveMatchesSelector,
  (entries) => {
    return entries.sortBy((entry) => entry.sportId)
      .first<RecordOfLiveStreamEntry>();
  },
);

export const selectLiveStreamPreferences = (state: ReduxState): LiveStreamState['preferences'] => {
  return selectLiveStreamState(state)?.preferences;
};

export const selectLiveStreamClosed = (state: ReduxState): LiveStreamState['preferences']['closed'] => {
  return selectLiveStreamPreferences(state)?.closed;
};

export const selectStreamId = (state: ReduxState): LiveStreamState['preferences']['streamId'] => {
  return selectLiveStreamPreferences(state).streamId;
};

export const selectedStream = createSelector(
  streamsByLiveMatchesSelector,
  (state: ReduxState) => selectStreamId(state),
  (entries, selectedMatchId) => {
    if (entries.size === 0 || !selectedMatchId) {
      return null;
    }

    return entries.get(selectedMatchId);
  },
);

export const selectHasLiveStream = (state: ReduxState, matchId: number): boolean => {
  return streamsByLiveMatchesSelector(state)?.has(matchId);
};
